/**
 * SeKa GTM DataLayer - E-Commerce Tracking
 * Shopify Standard Events mit dataLayer Push für Google Tag Manager
 * GA4-konformer E-Commerce Tracking Layer
 */

(function() {
  'use strict';

  // Initialize dataLayer
  window.dataLayer = window.dataLayer || [];

  const SeKaDataLayer = {
    /**
     * Clear ecommerce object before pushing new data
     */
    clearEcommerce: function() {
      dataLayer.push({ ecommerce: null });
    },

    /**
     * Format price from cents to decimal
     */
    formatPrice: function(priceInCents) {
      return parseFloat((priceInCents / 100).toFixed(2));
    },

    /**
     * Build GA4-compliant item object
     */
    buildItem: function(product, variant, index) {
      variant = variant || product.variants?.[0] || {};
      
      return {
        item_id: variant.sku || product.id?.toString() || '',
        item_name: product.title || '',
        item_brand: product.vendor || 'SeKa',
        item_category: product.type || product.product_type || '',
        item_category2: product.collections?.[0]?.title || '',
        item_variant: variant.title !== 'Default Title' ? variant.title : '',
        price: this.formatPrice(variant.price || product.price || 0),
        quantity: 1,
        index: index || 0
      };
    },

    /**
     * Build item from Shopify line item (cart)
     */
    buildCartItem: function(item, index) {
      return {
        item_id: item.sku || item.variant_id?.toString() || item.id?.toString() || '',
        item_name: item.product_title || item.title || '',
        item_brand: item.vendor || 'SeKa',
        item_category: item.product_type || '',
        item_variant: item.variant_title !== 'Default Title' ? item.variant_title : '',
        price: this.formatPrice(item.final_price || item.price || 0),
        quantity: item.quantity || 1,
        index: index || 0
      };
    },

    /**
     * EVENT: view_item_list
     * Triggered on collection/category pages
     */
    viewItemList: function(listName, products) {
      this.clearEcommerce();
      
      const items = products.map((product, index) => this.buildItem(product, null, index));
      
      dataLayer.push({
        event: 'view_item_list',
        ecommerce: {
          item_list_id: listName.toLowerCase().replace(/\s+/g, '_'),
          item_list_name: listName,
          items: items
        }
      });
      
      console.log('[GTM] view_item_list:', listName, items.length, 'items');
    },

    /**
     * EVENT: select_item
     * Triggered when user clicks on a product
     */
    selectItem: function(product, listName, index) {
      this.clearEcommerce();
      
      dataLayer.push({
        event: 'select_item',
        ecommerce: {
          item_list_id: listName?.toLowerCase().replace(/\s+/g, '_') || '',
          item_list_name: listName || '',
          items: [this.buildItem(product, null, index)]
        }
      });
      
      console.log('[GTM] select_item:', product.title);
    },

    /**
     * EVENT: view_item
     * Triggered on product detail pages
     */
    viewItem: function(product, variant) {
      this.clearEcommerce();
      
      const item = this.buildItem(product, variant, 0);
      
      dataLayer.push({
        event: 'view_item',
        ecommerce: {
          currency: window.SeKaGTM?.currency || 'EUR',
          value: item.price,
          items: [item]
        }
      });
      
      console.log('[GTM] view_item:', product.title);
    },

    /**
     * EVENT: add_to_cart
     * Triggered when product is added to cart
     */
    addToCart: function(item, quantity) {
      this.clearEcommerce();
      
      quantity = quantity || 1;
      const cartItem = this.buildCartItem(item, 0);
      cartItem.quantity = quantity;
      
      dataLayer.push({
        event: 'add_to_cart',
        ecommerce: {
          currency: window.SeKaGTM?.currency || 'EUR',
          value: cartItem.price * quantity,
          items: [cartItem]
        }
      });
      
      console.log('[GTM] add_to_cart:', item.product_title || item.title, 'qty:', quantity);
    },

    /**
     * EVENT: remove_from_cart
     * Triggered when product is removed from cart
     */
    removeFromCart: function(item, quantity) {
      this.clearEcommerce();
      
      quantity = quantity || 1;
      const cartItem = this.buildCartItem(item, 0);
      cartItem.quantity = quantity;
      
      dataLayer.push({
        event: 'remove_from_cart',
        ecommerce: {
          currency: window.SeKaGTM?.currency || 'EUR',
          value: cartItem.price * quantity,
          items: [cartItem]
        }
      });
      
      console.log('[GTM] remove_from_cart:', item.product_title || item.title, 'qty:', quantity);
    },

    /**
     * EVENT: view_cart
     * Triggered when cart is opened/viewed
     */
    viewCart: function(cart) {
      this.clearEcommerce();
      
      const items = cart.items.map((item, index) => this.buildCartItem(item, index));
      
      dataLayer.push({
        event: 'view_cart',
        ecommerce: {
          currency: window.SeKaGTM?.currency || 'EUR',
          value: this.formatPrice(cart.total_price),
          items: items
        }
      });
      
      console.log('[GTM] view_cart:', items.length, 'items, total:', this.formatPrice(cart.total_price));
    },

    /**
     * EVENT: begin_checkout
     * Triggered when checkout starts
     */
    beginCheckout: function(cart) {
      this.clearEcommerce();
      
      const items = cart.items.map((item, index) => this.buildCartItem(item, index));
      
      dataLayer.push({
        event: 'begin_checkout',
        ecommerce: {
          currency: window.SeKaGTM?.currency || 'EUR',
          value: this.formatPrice(cart.total_price),
          items: items
        }
      });
      
      console.log('[GTM] begin_checkout:', items.length, 'items');
    },

    /**
     * EVENT: purchase
     * Triggered on thank you page (order completion)
     */
    purchase: function(order) {
      this.clearEcommerce();
      
      const items = order.line_items.map((item, index) => ({
        item_id: item.sku || item.variant_id?.toString() || '',
        item_name: item.title || '',
        item_brand: item.vendor || 'SeKa',
        item_category: item.product_type || '',
        item_variant: item.variant_title || '',
        price: parseFloat(item.final_price || item.price || 0),
        quantity: item.quantity || 1,
        index: index
      }));
      
      dataLayer.push({
        event: 'purchase',
        ecommerce: {
          transaction_id: order.order_number?.toString() || order.id?.toString() || '',
          value: parseFloat(order.total_price || 0),
          tax: parseFloat(order.total_tax || 0),
          shipping: parseFloat(order.shipping_price || 0),
          currency: order.currency || window.SeKaGTM?.currency || 'EUR',
          coupon: order.discount_codes?.[0]?.code || '',
          items: items
        }
      });
      
      console.log('[GTM] purchase:', order.order_number, 'value:', order.total_price);
    },

    /**
     * EVENT: search
     * Triggered when user performs a search
     */
    search: function(searchTerm) {
      dataLayer.push({
        event: 'search',
        search_term: searchTerm
      });
      
      console.log('[GTM] search:', searchTerm);
    },

    /**
     * EVENT: view_search_results
     * Triggered on search results page
     */
    viewSearchResults: function(searchTerm, products, resultsCount) {
      this.clearEcommerce();
      
      const items = products.map((product, index) => this.buildItem(product, null, index));
      
      dataLayer.push({
        event: 'view_search_results',
        search_term: searchTerm,
        ecommerce: {
          items: items
        }
      });
      
      console.log('[GTM] view_search_results:', searchTerm, resultsCount, 'results');
    },

    /**
     * Page view with enhanced data
     */
    pageView: function(pageType, pageData) {
      dataLayer.push({
        event: 'page_view',
        page_type: pageType,
        page_title: document.title,
        page_location: window.location.href,
        page_path: window.location.pathname,
        ...pageData
      });
      
      console.log('[GTM] page_view:', pageType);
    },

    /**
     * User identification (for logged-in users)
     */
    identifyUser: function(customer) {
      if (!customer) return;
      
      dataLayer.push({
        event: 'user_data',
        user_id: customer.id?.toString() || '',
        user_email_hash: customer.email_hash || '',
        user_logged_in: true
      });
      
      console.log('[GTM] user_data: logged in');
    }
  };

  // Expose globally
  window.SeKaDataLayer = SeKaDataLayer;

  // Auto-identify user on load
  document.addEventListener('DOMContentLoaded', function() {
    if (window.SeKaGTM?.customer) {
      SeKaDataLayer.identifyUser(window.SeKaGTM.customer);
    }
  });

})();
